import { Alert, Button, Spinner, Text, VStack } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { PageContainer } from '../components/shared/PageContainer'
import { ReportParameterForm } from '../components/reporting/ReportParameterForm'
import { ReportRenderer } from '../components/reporting/ReportRenderer'
import { useCreateReportRun, useReportDefinitions, useReportPreview } from '../hooks/useReports'
import { useJobPolling } from '../hooks/useJobPolling'
import { extractApiMessage } from '../lib/apiError'

export function ReportRunnerPage() {
  const { key = '' } = useParams()
  const { data: definitions, isLoading } = useReportDefinitions()
  const preview = useReportPreview()
  const createRun = useCreateReportRun()
  const [jobId, setJobId] = useState<string | null>(null)
  const [lastParameters, setLastParameters] = useState<Record<string, string> | null>(null)
  const [jobError, setJobError] = useState<string | null>(null)
  const job = useJobPolling(jobId)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const definition = definitions?.find((d) => d.key === key)

  useEffect(() => {
    // Branch on !isTerminal ALONE — isTerminal can be true while job.data is undefined on an
    // HTTP-error path, and the stricter `!isTerminal || !job` guard hangs the UI forever.
    if (!job.isTerminal) return

    if (job.data?.status === 'Succeeded') {
      // Invalidate INSIDE the terminal Succeeded branch, never at the 202 accept — invalidating
      // at accept leaves stale data visible for up to the global 5-minute staleTime.
      queryClient.invalidateQueries({ queryKey: ['reports', 'runs'] })

      const runId = (job.data.result as { runId?: string } | undefined)?.runId
      if (runId) {
        navigate(`/dashboard/reports/runs/${runId}`)
      } else {
        // Succeeded but the result carried no runId — a malformed payload, not a job failure,
        // but silently stopping the spinner with no explanation is exactly the dead-end this
        // branch exists to avoid. Same error surface as the Failed branch below.
        setJobError('Could not generate this snapshot.')
      }
      setJobId(null)
      return
    }

    // Job-level Failed, or the poll itself terminated in error (e.g. a 404) — either way, surface
    // it and let the user retry rather than leaving the button stuck with no explanation (same
    // terminal-but-not-Succeeded handling as AutoGenerateModal/CampaignAutoGenerateModal).
    setJobError(job.data?.errorMessage ?? 'Could not generate this snapshot.')
    setJobId(null)
  }, [job.isTerminal, job.data, navigate, queryClient])

  if (isLoading) {
    return (
      <PageContainer title="Report">
        <VStack py={12}>
          <Spinner size="lg" color="accent" />
        </VStack>
      </PageContainer>
    )
  }

  if (!definition) {
    return (
      <PageContainer title="Report">
        <Text>That report does not exist.</Text>
      </PageContainer>
    )
  }

  if (definition.status !== 'Available') {
    return (
      <PageContainer title={definition.title}>
        <Text>This report is not available yet.</Text>
      </PageContainer>
    )
  }

  return (
    <PageContainer title={definition.title} subtitle={definition.question}>
      <VStack align="stretch" gap={6}>
        <ReportParameterForm
          key={definition.key}
          definition={definition}
          isBusy={preview.isPending}
          onSubmit={(parameters) => {
            setLastParameters(parameters)
            preview.mutate({ key: definition.key, parameters })
          }}
        />

        {!!preview.error && (
          // Surface the server's own message — cap errors say what to change, and replacing that
          // with "Please try again" is the live defect class in roadmap A10.
          <Alert.Root status="error">
            <Alert.Indicator />
            <Alert.Content>{extractApiMessage(preview.error, 'Could not build this report.')}</Alert.Content>
          </Alert.Root>
        )}

        {!!preview.data && <ReportRenderer result={preview.data} />}

        {(!!createRun.error || !!jobError) && (
          <Alert.Root status="error">
            <Alert.Indicator />
            <Alert.Content>
              {createRun.error ? extractApiMessage(createRun.error, 'Could not generate this snapshot.') : jobError}
            </Alert.Content>
          </Alert.Root>
        )}

        <Button
          colorPalette="brand"
          alignSelf={{ base: 'stretch', md: 'flex-start' }}
          width={{ base: 'full', md: 'auto' }}
          loading={createRun.isPending || (!!jobId && !job.isTerminal)}
          disabled={!preview.data || !lastParameters}
          onClick={() => {
            if (!lastParameters) return
            setJobError(null)
            createRun.mutate(
              { key: definition.key, parameters: lastParameters },
              { onSuccess: (r) => setJobId(r.jobId) },
            )
          }}
        >
          Generate snapshot
        </Button>
      </VStack>
    </PageContainer>
  )
}
